import { createSlice } from "@reduxjs/toolkit";
import { FormattedMessage } from "react-intl";
import axios from "utils/axios";
import { getErrorCode, openSnackbarWithError } from "utils/errors";
import {
  calculateTotalPrices,
  fromBackToFrontAlbum,
} from "views/albums/album-utils";
import { dispatch } from "../index";
import { addProduct, endOrder } from "./cart";
import { openSnackbar } from "./snackbar";

// ----------------------------------------------------------------------

const initialState = {
  error: null,
  albums: [],
  totalAlbumsCount: 0,
  isLoading: true,
  album: {
    error: null,
    data: null,
    isLoading: true,
    photos: [],
    totalPhotosCount: 0,
    isPhotosLoading: true,
    photosError: false,
    isSubscribing: false,
  },
  contestants: {
    isLoading: true,
    totalCount: 0,
    data: [],
    error: null,
  },
  contestant: {
    isLoading: true,
    data: null,
    error: null,
  },
  isCartConflictAlbum: false,
  conflictedProduct: null,
  order: {
    orderCreatedId: null,
    isInPaymentProcess: false,
    error: null,
    orderCreatedEmail: null,
  },
  photograph: {
    isLoading: true,
    isLoadingAlbum: true,
    data: null,
    error: false,
    albums: [],
    offset: 0,
    limit: 50,
    totalAlbumsCount: 0,
  },
  checkoutAlbum: null,
  isLoadingCheckoutAlbum: true,
  isCheckoutAlbumError: false,
  // ============
  isComputingTotalAndPackages: false,
  total: 0,
  packages: [],
  packAthlete: null,
  packAthletes: [],
  otherProducts: [],
  isRelativePhotosDialogOpen: false,
  relativePhotoKey: null,
};

const slice = createSlice({
  name: "ecommerce",
  initialState,
  reducers: {
    startLoading(state, action) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },

    openRelativePhotosDialog(state, action) {
      state.isRelativePhotosDialogOpen = true;
      state.relativePhotoKey = action.payload;
    },

    closeRelativePhotosDialog(state) {
      state.isRelativePhotosDialogOpen = false;
      state.relativePhotoKey = null;
    },

    //============ GET ALBUMS ===========

    getAlbums(state) {
      state.isLoading = true;
    },

    getAlbumsSuccess(state, action) {
      state.albums = action.payload.data;
      state.totalAlbumsCount = action.payload.total_count;
      state.isLoading = false;
    },

    getAlbumsFailure(state) {
      state.isLoading = false;
      state.albums = [];
      state.totalAlbumsCount = 0;
    },

    // ========== GET 1 ALBUM ============

    getAlbum(state) {
      state.album.isLoading = true;
      state.album.error = false;
    },

    getAlbumSuccess(state, action) {
      state.album.data = fromBackToFrontAlbum(action.payload);
      state.album.isLoading = false;
    },

    getAlbumFailure(state, action) {
      state.album.isLoading = false;
      state.album.data = null;
      state.album.error = true;
    },

    // ======== GET 1 ALBUM PHOTO ==========

    getAlbumPhotos(state) {
      state.album.isPhotosLoading = true;
    },

    getAlbumPhotosSuccess(state, action) {
      state.album.photos = action.payload.data;
      state.album.totalPhotosCount = action.payload.total_count;
      state.album.isPhotosLoading = false;
    },

    getAlbumPhotosFailure(state, action) {
      state.album.photos = [];
      state.album.totalPhotosCount = 0;
      state.album.isPhotosLoading = false;
      state.album.photosError = action.payload;
    },

    // ======== GET 1 ALBUM CONTESTANT =========

    getAlbumContestant(state) {
      state.contestant.isLoading = true;
    },

    getAlbumContestantSuccess(state, action) {
      state.contestant.isLoading = false;
      state.contestant.data = action.payload;
    },

    getAlbumContestantFailure(state, action) {
      state.contestant.isLoading = false;
      state.contestant.data = null;
      state.contestant.error = action.payload;
    },

    // ======== CART ========

    setConflictCartAlbum(state, action) {
      state.isCartConflictAlbum = action.payload.value;
      state.conflictedProduct = action.payload.conflictedProduct;
    },

    startOrderPayment(state, action) {
      state.order.isInPaymentProcess = true;
      state.order.error = null;
    },

    orderError(state, action) {
      state.order.isInPaymentProcess = false;
      state.order.error = action.payload;
    },

    orderCreated(state, action) {
      state.order = {
        isInPaymentProcess: false,
        error: null,
        orderCreatedId: action.payload.order_id,
        orderCreatedEmail: action.payload.order_email,
      };
    },

    closeConfirmationOrder(state, action) {
      state.order = {
        ...initialState.order,
      };
    },
    // ===== PHOTOGRAPH SECTION ======

    getPhotograph(state) {
      state.photograph.isLoading = true;
    },

    getPhotographSuccess(state, action) {
      state.photograph.isLoading = false;
      state.photograph.data = action.payload;
    },

    getPhotographFailure(state, action) {
      state.photograph.isLoading = false;
      state.photograph.error = true;
    },

    getPhotographAlbums(state) {
      state.photograph.isLoadingAlbum = true;
      state.photograph.error = false;
    },

    getPhotographAlbumsSuccess(state, action) {
      state.photograph.isLoadingAlbum = false;
      state.photograph.totalAlbumsCount = action.payload.total_count;
      state.photograph.albums = action.payload.data;
    },

    getPhotographAlbumsFailure(state, action) {
      state.photograph.isLoadingAlbum = false;
      state.photograph.albums = [];
      state.photograph.totalAlbumsCount = 0;
      state.photograph.error = true;
    },
    // ===== SUBSCRIBE =====

    subscribeAlbum(state) {
      state.album.isSubscribing = true;
    },

    subscribeAlbumSuccess(state, action) {
      state.album.isSubscribing = false;
    },

    subscribeAlbumFailure(state, action) {
      state.album.isSubscribing = false;
    },

    // ===== CHECKOUT ALBUM =====

    getCheckoutAlbum(state) {
      state.isLoadingCheckoutAlbum = true;
      state.isCheckoutAlbumError = false;
    },

    noCheckoutAlbum(state) {
      state.isLoadingCheckoutAlbum = false;
      state.isCheckoutAlbumError = false;
      state.checkoutAlbum = null;
    },

    getCheckoutAlbumSuccess(state, action) {
      state.isLoadingCheckoutAlbum = false;
      state.checkoutAlbum = fromBackToFrontAlbum(action.payload);
    },

    getCheckoutAlbumFailure(state, action) {
      state.isLoadingCheckoutAlbum = false;
      state.checkoutAlbum = null;
      state.isCheckoutAlbumError = true;
    },

    computePricesAndPackages(state, action) {
      state.isComputingTotalAndPackages = false;
    },
    computePricesAndPackagesSuccess(state, action) {
      state.isComputingTotalAndPackages = true;
      state.total = action.payload.total;
      state.packages = action.payload.packages;
      state.packAthlete = action.payload.packAthlete;
    },
    computePricesAndPackagesFailure(state, action) {
      state.isComputingTotalAndPackages = true;
    },

    setCToken(state, action) {
      state.cToken = action.payload;
    },

    // ======================
    getPublicAlbumContestants(state) {
      state.contestants.isLoading = true;
    },

    getPublicAlbumContestantsSuccess(state, action) {
      state.contestants.isLoading = false;
      state.contestants.totalCount = action.payload.total_count;
      state.contestants.data = action.payload.data;
    },

    getPublicAlbumContestantsFailure(state, action) {
      state.contestants.isLoading = false;
      state.contestants.error = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function openRelativePhotosDialog(photoKey) {
  return async () => {
    dispatch(slice.actions.openRelativePhotosDialog(photoKey));
  };
}

export function closeRelativePhotosDialog(photoKey) {
  return async () => {
    dispatch(slice.actions.closeRelativePhotosDialog(photoKey));
  };
}

export function getPublicAlbums(nameStartsWith, offset, limit = 100) {
  return async (_, getState) => {
    dispatch(slice.actions.getAlbums());
    // ==== TODO this when API will be able to receive CTOKEN... but keep the code
    //const cToken = await window.grecaptcha.execute('')
    // ====
    try {
      const response = await axios.get(`/api/v1/public/albums`, {
        params: {
          offset,
          limit,
          name_starts_with: nameStartsWith,
        },
      });
      dispatch(slice.actions.getAlbumsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumsFailure());
    }
  };
}

export function getPublicAlbumsByPhotographs(
  photographId,
  nameStartsWith,
  offset,
  limit = 100
) {
  return async (_, getState) => {
    dispatch(slice.actions.getAlbums());
    // ==== TODO this when API will be able to receive CTOKEN... but keep the code
    //const cToken = await window.grecaptcha.execute('')
    // ====
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${photographId}/albums`,
        {
          params: {
            offset,
            limit,
            name_starts_with: nameStartsWith,
          },
        }
      );
      dispatch(slice.actions.getAlbumsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumsFailure());
    }
  };
}

export function getPublicAlbumPhotos(
  photographUrlId,
  albumId,
  bibOrLastName,
  hasNoIdentification,
  tagId,
  offset,
  limit,
  ak
) {
  return async () => {
    dispatch(slice.actions.getAlbumPhotos());
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${photographUrlId}/albums/${albumId}/photos`,
        {
          params: {
            offset,
            limit,
            bib_or_last_name: bibOrLastName === "" ? null : bibOrLastName,
            identification_status: !hasNoIdentification ? null : "incorrect",
            tag_id: tagId,
            ak,
          },
        }
      );
      dispatch(slice.actions.getAlbumPhotosSuccess(response.data));
    } catch (error) {
      console.log(getErrorCode(error));
      dispatch(slice.actions.getAlbumPhotosFailure(getErrorCode(error)));
    }
  };
}

export function getPublicAlbumContestants(photographUrlId, albumId, filter) {
  return async () => {
    dispatch(slice.actions.getPublicAlbumContestants());
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${photographUrlId}/albums/${albumId}/contestants`,
        {
          params: {
            gender:
              filter.gender && filter.gender !== "all" ? filter.gender : null,
            offset: filter.page * filter.limit,
            limit: filter.limit,
          },
        }
      );
      dispatch(slice.actions.getPublicAlbumContestantsSuccess(response.data));
    } catch (error) {
      dispatch(
        slice.actions.getPublicAlbumContestantsFailure(getErrorCode(error))
      );
    }
  };
}

export function getPublicAlbumContestant(photographId, albumId, bibOrLastName) {
  return async () => {
    dispatch(slice.actions.getAlbumContestant());
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${photographId}/albums/${albumId}/contestants/${bibOrLastName}`,
        {
          validateStatus: (status) => {
            // Ignore bad request
            return status === 200 || status === 400 || status === 404;
          },
        }
      );
      dispatch(slice.actions.getAlbumContestantSuccess(response.data));
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.getAlbumContestantFailure(getErrorCode(error)));
    }
  };
}

export function getPublicPhotograph(urlId) {
  return async () => {
    dispatch(slice.actions.getPhotograph());
    try {
      const response = await axios.get(`/api/v1/public/photographs/${urlId}`);
      dispatch(slice.actions.getPhotographSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getPhotographFailure(error));
    }
  };
}

export function getPublicPhotographAlbums(
  urlId,
  searchBibText,
  offset,
  limit = 100
) {
  return async () => {
    dispatch(slice.actions.getPhotographAlbums());
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${urlId}/albums`,
        {
          params: {
            offset,
            limit,
          },
        }
      );
      dispatch(slice.actions.getPhotographAlbumsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getPhotographAlbumsFailure(error));
    }
  };
}

export function getPublicAlbum(albumId, accessKey) {
  return async () => {
    dispatch(slice.actions.getAlbum());
    try {
      const response = await axios.get(
        `/api/v1/public/albums/${albumId}?ak=${accessKey}`
      );
      dispatch(slice.actions.getAlbumSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumFailure());
    }
  };
}

export function getPublicAlbumByPhotograph(photographId, albumId, accessKey) {
  return async () => {
    dispatch(slice.actions.getAlbum());
    try {
      const response = await axios.get(
        `/api/v1/public/photographs/${photographId}/albums/${albumId}?ak=${accessKey}`
      );
      dispatch(slice.actions.getAlbumSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumFailure());
    }
  };
}

export function setConflictCartAlbum(value, conflictedProduct = null) {
  return async () => {
    dispatch(
      slice.actions.setConflictCartAlbum({
        value,
        conflictedProduct,
      })
    );
  };
}

export function addConflictedProduct() {
  return async (dispatch, getState) => {
    const { ecommerce } = getState();
    dispatch(addProduct(ecommerce.conflictedProduct));
    dispatch(setConflictCartAlbum(false, null));
  };
}

export function startLoadingAlbum() {
  return async () => {
    dispatch(slice.actions.startLoading());
  };
}

export function handleFreePayment(email) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.startOrderPayment());
      const { cart, ecommerce } = getState();
      const sanitizedPhotos = cart.products.map((product) => ({
        type: product.type,
        key: product.key,
        package_id: product.package_id,
        quantity: product.quantity,
        contestant_id: product.contestant ? product.contestant.id : null,
        pack_selfie_id: product.pack_selfie_id,
      }));
      const { data } = await axios.post("/api/v1/public/create-payment", {
        email: email,
        photograph_album_id: cart.albumId,
        total_price: Number((ecommerce.total * 100).toFixed(0)),
        products: sanitizedPhotos,
      });
      dispatch(
        slice.actions.orderCreated({
          order_id: data.order_id,
          order_email: data.order_email,
        })
      );
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="order.created" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      dispatch(endOrder());
    } catch (err) {
      const errorCode = getErrorCode(err);
      console.error(err);
      dispatch(slice.actions.orderError(errorCode));
    }
  };
}

export function handlePayment(cardElement, stripe) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startOrderPayment());
    const { cart, ecommerce } = getState();
    try {
      const sanitizedPhotos = cart.products.map((product) => ({
        type: product.type,
        key: product.key,
        package_id: product.package_id,
        quantity: product.quantity,
        contestant_id: product.contestant ? product.contestant.id : null,
        pack_selfie_id: product.pack_selfie_id,
      }));
      const { data } = await axios.post("/api/v1/public/create-payment", {
        email: cart.email,
        phone: cart.address.phone,
        first_name: cart.address.first_name,
        last_name: cart.address.last_name,
        address1: cart.address.address1,
        address2: cart.address.address2,
        postal_code: cart.address.postal_code,
        city: cart.address.city,
        country: cart.address.country,
        photograph_album_id: cart.albumId,
        total_price: Number((ecommerce.total * 100).toFixed(0)),
        products: sanitizedPhotos,
      });

      if (!data.is_free_order) {
        const paymentMethodReq = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });
        if (paymentMethodReq.error) {
          dispatch(slice.actions.orderError(paymentMethodReq.error.message));
          return;
        }
        const { error } = await stripe.confirmCardPayment(data.client_secret, {
          payment_method: paymentMethodReq.paymentMethod.id,
        });
        window.gtag("event", "purchase", {
          value: cart.total,
          email: cart.email,
          country: cart.address.country,
          photograph_album_id: cart.albumId,
        });
        if (error) {
          dispatch(slice.actions.orderError(error.message));
          return;
        }
      }
      dispatch(
        slice.actions.orderCreated({
          order_id: data.order_id,
          order_email: data.order_email,
        })
      );
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="order.created" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      dispatch(endOrder());
    } catch (err) {
      const errorCode = getErrorCode(err);
      dispatch(slice.actions.orderError(errorCode));
    }
  };
}

export function closeConfirmationOrder() {
  return async () => {
    dispatch(slice.actions.closeConfirmationOrder());
  };
}

export function subscribeToAlbum(
  photographId,
  albumId,
  email,
  bib,
  navigate,
  captchaValue
) {
  return async (dispatch) => {
    dispatch(slice.actions.subscribeAlbum());
    try {
      await axios.post(
        `/api/v1/public/photographs/${photographId}/albums/${albumId}/subscribe`,
        {
          email,
          bib,
          token: captchaValue,
        }
      );
      dispatch(slice.actions.subscribeAlbumSuccess());
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="user_subscription.subscribed" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      setTimeout(() => {
        navigate();
      }, 8000);
    } catch (error) {
      dispatch(slice.actions.subscribeAlbumFailure());
      const code = getErrorCode(error);
      if (code === "BadRequest") {
        dispatch(
          openSnackbar({
            open: true,
            message: (
              <FormattedMessage id="user_subscription.already_subscribed" />
            ),
            variant: "alert",
            alert: {
              color: "error",
            },
            close: false,
          })
        );
      } else {
        openSnackbarWithError(error, dispatch);
      }
    }
  };
}

export function getAlbumTags(albumId) {
  return async () => {
    dispatch(slice.actions.getAlbumTags());
    try {
      const response = await axios.get(`/api/v1/public/albums/${albumId}/tags`);
      dispatch(slice.actions.getAlbumTagsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumTagsFailure());
    }
  };
}

export function getCheckoutAlbum(albumId) {
  return async (dispatch) => {
    dispatch(slice.actions.getCheckoutAlbum());
    try {
      const response = await axios.get(`/api/v1/public/albums/${albumId}`);
      dispatch(slice.actions.getCheckoutAlbumSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getCheckoutAlbumFailure());
    }
  };
}

export function noCheckoutAlbum() {
  return async () => {
    console.log("no checkout album");
    dispatch(slice.actions.noCheckoutAlbum());
  };
}

export function computePricesAndPackages(products, checkoutAlbum) {
  return async () => {
    dispatch(slice.actions.computePricesAndPackages());
    const { total, packages, packAthlete } = calculateTotalPrices(
      products,
      checkoutAlbum
    );
    dispatch(
      slice.actions.computePricesAndPackagesSuccess({
        total,
        packages,
        packAthlete,
      })
    );
  };
}
